import { type ForwardedRef } from 'react';

import * as api from '@gitbook/api-client';
import { getSiteProductDefinition, isSiteTypeDowngrade } from '@gitbook/app-pricing';
import { Badge, type BadgeKind, type BadgeProps } from '@gitbook/spine-react';

interface SiteTypeBadgeProps extends Omit<BadgeProps, 'label'> {
    type: api.SiteType;
    appliedType: api.SiteType;
}

export function SiteTypeBadge({
    ref,
    ...props
}: SiteTypeBadgeProps & { ref?: ForwardedRef<HTMLDivElement> }) {
    const label = getLabel(props);
    const kind = determineKind(props);
    const { type, appliedType, size = 'small', ...otherProps } = props;

    return <Badge ref={ref} label={label} kind={kind} size={size} {...otherProps} />;
}

function getLabel(props: SiteTypeBadgeProps): string {
    const { type, appliedType } = props;
    const typeDefinition = getSiteProductDefinition(type);
    const appliedTypeDefinition = getSiteProductDefinition(appliedType);

    if (type !== appliedType) {
        return `${typeDefinition.title} → ${appliedTypeDefinition.title}`;
    }

    return appliedTypeDefinition.title;
}

function determineKind(props: SiteTypeBadgeProps): BadgeKind {
    const { type, appliedType } = props;

    // If the site has been downgraded, show a danger badge
    if (isSiteTypeDowngrade(type, appliedType)) {
        return 'downgraded';
    }

    switch (appliedType) {
        case api.SiteType.Ultimate:
            return 'ultimate';
        case api.SiteType.Premium:
            return 'premium';
        case api.SiteType.Basic:
            return 'basic';
        default:
            return 'secondary';
    }
}
